import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function CapstoneTaskCheckbox({ task, onToggle }) {
  const [isCompleted, setIsCompleted] = useState(task.is_completed);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if this is a module task (read-only)
  const isModuleTask = task.type === 'module_task';

  // Sync state when task prop changes (e.g., after refresh)
  useEffect(() => {
    setIsCompleted(task.is_completed);
  }, [task.is_completed]);

  const handleToggle = async () => {
    // Module tasks cannot be toggled directly - they're completed by finishing the module
    if (isModuleTask) {
      toast.error('Complete the module to check off this task', {
        icon: '📚',
      });
      return;
    }

    if (loading) return;

    setLoading(true);
    const previousState = isCompleted;
    
    // Optimistic update
    setIsCompleted(!previousState);

    try {
      const response = await api.post(`/capstone-tasks/${task.id}/toggle`);
      
      if (response.data.success) {
        toast.success(
          response.data.is_completed 
            ? `✅ Task completed: ${task.title}` 
            : `Task unmarked: ${task.title}`
        );
        
        // Notify parent component to refresh progress
        if (onToggle) {
          onToggle(task.id, response.data.is_completed);
        }
      }
    } catch (error) {
      console.error('Error toggling task:', error);
      // Revert on error
      setIsCompleted(previousState);
      toast.error('Failed to update task status');
    } finally {
      setLoading(false);
    }
  };

  const getPhaseColor = (phase) => {
    const colors = {
      setup: 'bg-blue-100 text-blue-800',
      implementation: 'bg-purple-100 text-purple-800',
      testing: 'bg-yellow-100 text-yellow-800',
      deployment: 'bg-green-100 text-green-800',
    };
    return colors[phase] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`border border-gray-200 rounded-lg p-4 transition-colors ${
      isModuleTask ? 'bg-blue-50' : 'hover:bg-gray-50'
    }`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={handleToggle}
          disabled={loading || isModuleTask}
          title={isModuleTask ? 'Complete the module to check off this task' : ''}
          className={`
            flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center
            transition-all duration-200 mt-0.5
            ${loading || isModuleTask ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${isCompleted 
              ? 'bg-indigo-600 border-indigo-600' 
              : isModuleTask 
                ? 'border-blue-400 bg-white'
                : 'border-gray-300 hover:border-indigo-400'
            }
          `}
        >
          {isCompleted && (
            <Check className="w-4 h-4 text-white animate-in zoom-in duration-200" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-left flex-1"
            >
              <h4 className={`font-medium ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                {task.title}
                {isModuleTask && (
                  <span className="ml-2 text-xs text-blue-600 font-normal">
                    (Complete the module)
                  </span>
                )}
              </h4>
            </button>
            
            {/* Phase Badge */}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPhaseColor(task.phase)}`}>
              {task.phase}
            </span>
          </div>

          {/* Time estimate */}
          {task.estimated_minutes && (
            <p className="text-xs text-gray-500 mb-1">
              ⏱️ ~{task.estimated_minutes} min
            </p>
          )}

          {/* Description (collapsible) */}
          {task.description && (
            <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
              <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                {task.description}
              </p>
            </div>
          )}

          {/* Expand/Collapse indicator */}
          {task.description && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-indigo-600 hover:text-indigo-700 mt-1 font-medium"
            >
              {isExpanded ? '▲ Show less' : '▼ Show more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
